import * as XLSX from 'xlsx';
import {
  PROGRAM_UNIT_LABELS,
  type CommittedMatrix,
} from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  CommitmentNote,
  ExporterReceiver,
} from 'common/src/utils/types';

type T = (key: string, options?: Record<string, unknown>) => string;

/** Columnas de semana del Excel: "S42". La carga masiva lee este mismo formato. */
export const weekColumn = (week: number) => `S${week}`;
export const CLIENT_COLUMN = 'Cliente';

const round = (value: number) => Math.round(value * 10000) / 10000;

/**
 * Programa vigente en Excel: una fila por cliente, una columna por semana.
 * Sirve para compartirlo y también como plantilla para la carga masiva.
 */
export function exportProgramToExcel({
  program,
  receivers,
  weeks,
  matrix,
  notes,
  season,
  speciesName,
  t,
  emptyTemplate = false,
}: {
  program: CommercialProgram;
  receivers: ExporterReceiver[];
  weeks: number[];
  matrix: CommittedMatrix;
  notes: CommitmentNote[];
  season: string;
  speciesName: string;
  t: T;
  emptyTemplate?: boolean;
}) {
  const header = [
    CLIENT_COLUMN,
    ...weeks.map(weekColumn),
    ...(emptyTemplate ? [] : ['Total']),
  ];
  const rows = receivers.map((r) => {
    const values = weeks.map((w) =>
      emptyTemplate ? null : round(matrix.get(r.id)?.get(w) ?? 0) || null
    );
    const total = values.reduce<number>((sum, v) => sum + (v || 0), 0);
    return [r.name, ...values, ...(emptyTemplate ? [] : [round(total)])];
  });
  if (!emptyTemplate) {
    const weekTotals = weeks.map((_, i) =>
      round(rows.reduce((sum, row) => sum + (Number(row[i + 1]) || 0), 0))
    );
    rows.push([
      'TOTAL',
      ...weekTotals,
      round(weekTotals.reduce((a, b) => a + b, 0)),
    ]);
  }

  const unit = t(PROGRAM_UNIT_LABELS[program.unit]);
  const basis =
    program.date_basis === 'eta'
      ? t('Semana de arribo (ETA)')
      : t('Semana de despacho (ETD)');
  const sheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
  sheet['!cols'] = [{ wch: 32 }, ...weeks.map(() => ({ wch: 8 })), { wch: 10 }];

  const info = XLSX.utils.aoa_to_sheet([
    [t('Programa comercial')],
    [t('Especie'), speciesName],
    [t('Temporada'), season],
    [t('Unidad'), unit],
    [t('Fecha base'), basis],
    [t('Kilos por caja equivalente'), Number(program.kg_per_ceq)],
    [
      t('Kilos por embarque'),
      program.kg_per_shipment ? Number(program.kg_per_shipment) : '-',
    ],
  ]);
  info['!cols'] = [{ wch: 28 }, { wch: 28 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, t('Programa'));
  if (!emptyTemplate && notes.length > 0) {
    const nameOf = new Map(receivers.map((r) => [r.id, r.name]));
    const notesSheet = XLSX.utils.aoa_to_sheet([
      [CLIENT_COLUMN, t('Semana'), t('Nota'), t('Autor')],
      ...notes
        .filter((n) => nameOf.has(n.exporter_receiver_id))
        .map((n) => [
          nameOf.get(n.exporter_receiver_id),
          n.week == null ? t('General') : weekColumn(n.week),
          n.content,
          n.author || '',
        ]),
    ]);
    notesSheet['!cols'] = [{ wch: 32 }, { wch: 10 }, { wch: 60 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, notesSheet, t('Notas'));
  }
  XLSX.utils.book_append_sheet(workbook, info, t('Información'));

  const name = emptyTemplate
    ? t('Plantilla programa comercial')
    : t('Programa comercial');
  XLSX.writeFile(
    workbook,
    `${name} ${speciesName} ${season}.xlsx`.replace(/\s+/g, ' ').trim()
  );
}
